import { v4 as uuidv4 } from 'uuid';

import { getConferenceSessionId, getPersistentUserId } from '../base/jwt/functions';
import { getParticipantById, getParticipants } from '../base/participants/functions';

/**
 * Where a guest's identity is kept. Session storage, so it belongs to the tab:
 * it survives a reload and a reconnect, which is what makes a returning guest
 * the same person, and it does not follow them into a new tab.
 */
const GUEST_PERSON_ID_KEY = 'meethour.guest-person-id';
let guestPersonId;

/**
 * Returns this tab's guest identity, creating it on first use. The prefix keeps
 * it from ever colliding with a numeric account id from a token.
 *
 * It is self-asserted by nature and scoped to the tab, so a guest is one person
 * per tab: a second tab is a second guest, and a closed tab's identity is gone.
 * That is enough for colors and reconnect detection, which is all it is used
 * for.
 *
 * @returns {string}
 */
export function getGuestPersonId() {
    if (guestPersonId) {
        return guestPersonId;
    }

    try {
        guestPersonId = globalThis.sessionStorage?.getItem(GUEST_PERSON_ID_KEY);
    } catch {
        // Storage can be unavailable in privacy-restricted contexts.
    }

    if (!guestPersonId) {
        guestPersonId = `guest-${uuidv4()}`;

        try {
            globalThis.sessionStorage?.setItem(GUEST_PERSON_ID_KEY, guestPersonId);
        } catch {
            // The in-memory id still correlates reconnects within this page.
        }
    }

    return guestPersonId;
}

/**
 * Returns the id of the person behind this client: the account id when signed
 * in, otherwise the browser's guest identity.
 *
 * @param {Object} state - Redux state.
 * @returns {string|undefined}
 */
export function getPersonId(state: Object) {
    const userId = getPersistentUserId(state);

    if (userId) {
        return userId;
    }

    if (state['features/base/config']?.sessionPersistence?.includeGuests === false) {
        return undefined;
    }

    return getGuestPersonId();
}

/**
 * Returns a human readable label for the device this client runs on. It is
 * only used to tell a person's own endpoints apart in the UI.
 *
 * @returns {string}
 */
export function getLocalDeviceLabel() {
    const { navigator } = globalThis;

    if (navigator?.product === 'ReactNative') {
        return 'Mobile app';
    }

    return navigator?.platform || 'Web browser';
}

/**
 * Returns the session persistence options which {@code lib-meet-hour} puts into
 * this endpoint's presence.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
export function getSessionPersistenceOptions(state: Object) {
    const config = state['features/base/config'] ?? {};
    const userId = getPersonId(state);

    return {
        deviceLabel: config.sessionPersistence?.deviceLabel || getLocalDeviceLabel(),
        enabled: Boolean(userId) && config.sessionPersistence?.enabled !== false,
        sessionId: getConferenceSessionId(),
        userId
    };
}

/**
 * Returns the key which identifies the person behind a participant across
 * reconnects, tabs and devices. Falls back to the endpoint id for participants
 * without a stable identity, which keeps the previous per-endpoint behavior.
 *
 * @param {Object} participant - Redux participant.
 * @returns {string|undefined}
 */
export function getParticipantIdentityKey(participant: Object) {
    if (!participant) {
        return undefined;
    }

    return participant.persistentUserId || participant.id;
}

/**
 * Same as {@link getParticipantIdentityKey}, but resolves the local
 * participant's identity from the token as well. The local participant learns
 * its own identity only once the conference is joined, while colors and
 * avatars are already needed before that.
 *
 * @param {Object} state - Redux state.
 * @param {Object} participant - Redux participant.
 * @returns {string|undefined}
 */
export function getIdentityKeyOf(state: Object, participant: Object) {
    if (participant?.local) {
        return getPersonId(state) || participant.id;
    }

    return getParticipantIdentityKey(participant);
}

/**
 * Returns whether the person behind a participant has another endpoint in the
 * conference besides the given one.
 *
 * This is what separates a reconnect from an arrival: if somebody's tile is
 * still on screen when a new endpoint of theirs shows up, they never left as
 * far as the room is concerned.
 *
 * @param {Object} state - Redux state.
 * @param {Object|string} participantOrId - Participant or participant id.
 * @returns {boolean}
 */
export function hasOtherSessionsOf(state: Object, participantOrId: Object | string) {
    // The local endpoint counts here: when my phone drops off, I am still in
    // the meeting on this laptop, so nobody arrived or left.
    return _otherSessionsOf(state, participantOrId, true).length > 0;
}

/**
 * Returns the other endpoints of the person behind a participant.
 *
 * @param {Object} state - Redux state.
 * @param {Object|string} participantOrId - Participant or participant id.
 * @param {boolean} includeLocal - Whether this client's own endpoint counts as
 * one of them. It must not when the caller is about to act on the result.
 * @private
 * @returns {Array<Object>}
 */
function _otherSessionsOf(state, participantOrId, includeLocal) {
    const participant = typeof participantOrId === 'string'
        ? getParticipantById(state, participantOrId)
        : participantOrId;
    const { id, persistentUserId } = participant ?? {};

    if (!persistentUserId) {
        return [];
    }

    return getParticipants(state).filter(p =>
        p.id !== id
        && (includeLocal || !p.local)
        && p.persistentUserId === persistentUserId);
}

/**
 * Returns every participant which is the same person as the given one,
 * including that participant itself. The result is ordered like the
 * participants state, so the first entry is the person's oldest endpoint.
 *
 * @param {Object} state - Redux state.
 * @param {Object|string} participantOrId - Participant or participant id.
 * @returns {Array<Object>}
 */
export function getParticipantSessions(state: Object, participantOrId: Object | string) {
    const participant = typeof participantOrId === 'string'
        ? getParticipantById(state, participantOrId)
        : participantOrId;
    const { persistentUserId } = participant ?? {};

    if (!persistentUserId) {
        return participant ? [ participant ] : [];
    }

    return getParticipants(state).filter(p => p.persistentUserId === persistentUserId);
}

/**
 * Returns whether the UI should point out that somebody is here from several
 * devices. Off by default: moderation applies to a person as a whole, so the
 * split into devices is not something a participant has to act on.
 *
 * @param {Object} state - Redux state.
 * @returns {boolean}
 */
export function areDeviceIndicatorsEnabled(state: Object) {
    return state['features/base/config']?.sessionPersistence?.showDeviceIndicators === true;
}

/**
 * Returns the endpoint ids of the other devices of the person behind a
 * participant. Moderation is applied to a person, not to one of their windows,
 * so every moderator action is repeated for these.
 *
 * @param {Object} state - Redux state.
 * @param {string} participantId - Endpoint the moderator acted on.
 * @returns {Array<string>}
 */
export function getOtherSessionIdsOf(state: Object, participantId: string) {
    // Never moderate this client's own endpoint as a side effect of moderating
    // somebody else's, even when it is the same person on another device.
    return _otherSessionsOf(state, participantId, false).map(p => p.id);
}

/**
 * Collapses a participant list so that a signed in person appears once, with
 * their remaining endpoints kept alongside. Order is preserved: a group takes
 * the position of the person's oldest endpoint.
 *
 * @param {Array<Object>} participants - Participants to group.
 * @param {boolean} merge - Whether to merge endpoints of one person. Without
 * device indicators nothing tells the viewer a row stands for several
 * endpoints, so merging would silently hide people.
 * @returns {Array<{key: string, primary: Object, sessions: Array<Object>}>}
 */
export function groupParticipantsBySession(participants: Array<Object> = [], merge: boolean = true) {
    const groups = [];
    const byIdentity = new Map();

    for (const participant of participants) {
        const key = merge ? getParticipantIdentityKey(participant) : participant.id;
        const existing = merge && participant.persistentUserId ? byIdentity.get(key) : undefined;

        if (existing) {
            existing.sessions.push(participant);
        } else {
            const group = {
                key,
                primary: participant,
                sessions: [ participant ]
            };

            groups.push(group);

            if (participant.persistentUserId) {
                byIdentity.set(key, group);
            }
        }
    }

    return groups;
}

/**
 * Returns the stale endpoints left behind by a reconnect: participants of the
 * same person and the same client session which are not the joining endpoint.
 *
 * A client session is one browser tab or one app process, so a second entry for
 * one session is an endpoint whose unavailable presence was lost or has not
 * arrived yet. The one exception is a duplicated/restored browser tab, which
 * inherits the session storage of its origin tab; deployments where Prosody
 * already replaces stale endpoints can switch this fallback off with
 * {@code sessionPersistence.removeStaleEndpoints: false}.
 *
 * @param {Object} state - Redux state.
 * @param {Object} participant - The participant which just joined.
 * @returns {Array<Object>}
 */
export function getStaleSessionsOf(state: Object, participant: Object) {
    const { id, persistentUserId, sessionId } = participant ?? {};

    if (!persistentUserId
            || !sessionId
            || state['features/base/config']?.sessionPersistence?.removeStaleEndpoints === false) {
        return [];
    }

    return getParticipants(state).filter(p =>
        p.id !== id
        && !p.local
        && p.persistentUserId === persistentUserId
        && p.sessionId === sessionId);
}
